import React, { useState, useEffect } from 'react';
import { Package, X, Save, History } from 'lucide-react';
import { useData } from '../context/DataContext';
import { useT } from '../context/LanguageContext';
import { carsysApi } from '../api/carsysApi';
import { sentenceCase } from '../lib/casing';
import { toast } from 'sonner';

export default function OnderdeelEditor({ onderdeel, onClose }) {
  const { t } = useT();
  const { createOnderdeel, updateOnderdeel } = useData();
  const isNew = !onderdeel || !onderdeel.id;
  const [data, setData] = useState(() => onderdeel || { code: '', omschrijving: '', voorraad: 0, minVoorraad: 0, inkoop: 0, verkoop: 0, locatie: '' });
  const [historie, setHistorie] = useState([]);
  const [showHist, setShowHist] = useState(false);
  const upd = (k, v) => setData(d => ({ ...d, [k]: v }));
  const eur = (n) => (parseFloat(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });

  useEffect(() => {
    if (!isNew && showHist) {
      carsysApi.onderdeelHistorie(data.id).then(setHistorie).catch(() => {});
    }
  }, [showHist, isNew, data.id]);

  const handleSave = async () => {
    if (!data.code) { toast.error(t('Code is verplicht')); return; }
    try {
      const payload = { ...data, voorraad: +data.voorraad, minVoorraad: +data.minVoorraad, inkoop: +data.inkoop, verkoop: +data.verkoop };
      if (isNew) await createOnderdeel(payload);
      else await updateOnderdeel(data.id, payload);
      toast.success(t('Onderdeel opgeslagen'));
      onClose();
    } catch { toast.error(t('Opslaan mislukt')); }
  };

  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}><Package size={16} style={{ verticalAlign: 'middle' }} /> {isNew ? t('Nieuw onderdeel', 'Nieuw onderdeel') : t('Onderdeel bewerken', 'Onderdeel bewerken')}</h3>
          <button className="cs-btn ghost" onClick={onClose}><X size={14} /></button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label className="cs-section-title">{t('Code')}</label><input className="cs-input" value={data.code} onChange={e => upd('code', e.target.value)} /></div>
          <div><label className="cs-section-title">{t('Locatie')}</label><input className="cs-input" value={data.locatie} onChange={e => upd('locatie', e.target.value)} /></div>
          <div style={{ gridColumn: '1 / 3' }}><label className="cs-section-title">{t('Omschrijving')}</label><input className="cs-input" value={data.omschrijving} onChange={e => upd('omschrijving', e.target.value)} onBlur={e => upd('omschrijving', sentenceCase(e.target.value))} /></div>
          <div><label className="cs-section-title">{t('Voorraad')}</label><input type="number" className="cs-input" value={data.voorraad} onChange={e => upd('voorraad', e.target.value)} /></div>
          <div><label className="cs-section-title">{t('Min voorraad', 'Min voorraad')}</label><input type="number" className="cs-input" value={data.minVoorraad} onChange={e => upd('minVoorraad', e.target.value)} /></div>
          <div><label className="cs-section-title">{t('Inkoopprijs', 'Inkoopprijs')}</label><input type="number" step="0.01" className="cs-input" value={data.inkoop} onChange={e => upd('inkoop', e.target.value)} /></div>
          <div><label className="cs-section-title">{t('Verkoopprijs', 'Verkoopprijs')}</label><input type="number" step="0.01" className="cs-input" value={data.verkoop} onChange={e => upd('verkoop', e.target.value)} /></div>
        </div>
        {!isNew && (
          <div style={{ marginTop: 12 }}>
            <button className="cs-btn ghost" onClick={() => setShowHist(s => !s)}><History size={12} /> {t('Prijshistorie', 'Prijshistorie')} {showHist ? t('verbergen', 'verbergen') : t('tonen', 'tonen')}</button>
            {showHist && (historie.length === 0
              ? <div style={{ fontSize: 12, color: 'var(--cs-text-muted)', marginTop: 6 }}>{t('Geen historie - prijs is nog niet aangepast.', 'Geen historie - prijs is nog niet aangepast.')}</div>
              : <table className="cs-table" style={{ marginTop: 8 }}>
                  <thead><tr><th>{t('Datum')}</th><th>{t('Oud inkoop', 'Oud inkoop')}</th><th>{t('Nieuw inkoop', 'Nieuw inkoop')}</th><th>{t('Oud verkoop', 'Oud verkoop')}</th><th>{t('Nieuw verkoop', 'Nieuw verkoop')}</th></tr></thead>
                  <tbody>{historie.map(h => <tr key={h.id}><td>{h.datum}</td><td>{eur(h.oudInkoop)}</td><td>{eur(h.nieuwInkoop)}</td><td>{eur(h.oudVerkoop)}</td><td>{eur(h.nieuwVerkoop)}</td></tr>)}</tbody>
                </table>)}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button className="cs-btn" onClick={onClose}>{t('Annuleren')}</button>
          <button className="cs-btn primary" onClick={handleSave}><Save size={14} /> {t('Opslaan')}</button>
        </div>
      </div>
      <style>{`
        .cs-modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.55); display: flex; align-items: center; justify-content: center; z-index: 100; }
        .cs-modal { background: #fff; border-radius: 10px; padding: 20px 22px; width: 640px; max-width: 92vw; box-shadow: 0 20px 60px rgba(0,0,0,0.25); }
      `}</style>
    </div>
  );
}
